/**
 * Brief 185 — renders the acting credential that drove an activity row: a
 * connected MCP agent (by its label when known), an API key, a dashboard
 * session ("You"), or an x402 wallet. Null actor (pre-attribution rows) → "—".
 */
import { Bot, KeyRound, User, Wallet } from 'lucide-react';
import type { ActivityActor } from '@/lib/queries';

export interface ActorLabels {
  /** connectionId → display label, for mcp_connection actors. */
  connections?: Record<string, string>;
  /** The signed-in customer's id, so a 'session' actor reads "You". */
  selfId?: string | null;
}

export function actorLabel(actor: ActivityActor, labels: ActorLabels = {}): string {
  switch (actor.kind) {
    case 'mcp_connection':
      return (actor.id && labels.connections?.[actor.id]) || 'Connected agent';
    case 'api_key':
      return 'API key';
    case 'session':
      return 'You (dashboard)';
    case 'x402_wallet':
      return actor.id ? `${actor.id.slice(0, 6)}…${actor.id.slice(-4)}` : 'Wallet';
    default:
      return '—';
  }
}

export function ActorBadge({ actor, labels }: { actor: ActivityActor; labels?: ActorLabels }) {
  const text = actorLabel(actor, labels);
  const Icon =
    actor.kind === 'mcp_connection'
      ? Bot
      : actor.kind === 'api_key'
        ? KeyRound
        : actor.kind === 'session'
          ? User
          : actor.kind === 'x402_wallet'
            ? Wallet
            : null;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      {Icon ? <Icon className="h-3 w-3" /> : null}
      {text}
    </span>
  );
}
